import * as React from 'react'
import { useLocation } from 'react-router-dom'

import { useReducedMotion } from 'motion/react'
import * as m from 'motion/react-m'

import { IconButton, XIcon } from '@buffer-mono/popcorn'
import type { Uploader } from '@buffer-mono/uploader'

import { useOrganizationLimits } from '~publish/contexts/AccountContext'
import { AIAssistant } from '~publish/legacy/ai/components/AIAssistant/AIAssistant'
import {
  selectAIAssistantOpened,
  toggleAIAssistant,
} from '~publish/legacy/ai/state/slice'
import { useAppDispatch, useAppSelector } from '~publish/legacy/store'
import { useIdeasCount } from '~publish/pages/Create/hooks/useIdeasCount'

import { ConfirmCloseModal } from '../ConfirmCloseModal'
import { useIdeaComposerEditor, useIdeaComposerState } from '../IdeaComposer'
import { IdeaEditor } from '../IdeaEditor'
import { IdeasLimitReachedModal } from '../IdeasLimitReachedModal'
import { isIdeaComposerLocationState } from '../../IdeaManagementRouter/hooks'

import styles from './IdeaComposerPanel.module.css'

import type { JSX } from 'react'

/**
 * Covers the panel's composer content while the AI Assistant is open. The
 * panel has no room for the flanking side panel the dialog uses, so the
 * assistant overlays the editor instead and toggles back and forth.
 */
const AIAssistantPanelOverlay = (): JSX.Element | null => {
  const dispatch = useAppDispatch()
  const editor = useIdeaComposerEditor()
  const isAIAssistantOpen = useAppSelector(selectAIAssistantOpened)

  const closeAIAssistant = React.useCallback((): void => {
    dispatch(toggleAIAssistant(false))
  }, [dispatch])

  // Close and reset content generation state when the panel unmounts.
  React.useEffect(() => {
    return (): void => {
      dispatch(toggleAIAssistant(false))
    }
  }, [dispatch])

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      closeAIAssistant()
    }
  }

  if (!isAIAssistantOpen) {
    return null
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={styles.aiAssistantOverlay}
      data-testid="idea-composer-panel-ai-overlay"
      onKeyDown={handleKeyDown}
    >
      <IconButton
        className={styles.aiAssistantClose}
        label="Close AI Assistant"
        variant="tertiary"
        size="small"
        onClick={closeAIAssistant}
      >
        <XIcon />
      </IconButton>
      <AIAssistant editor={editor} />
    </div>
  )
}

type IdeaComposerPanelProps = {
  onClose: () => void
  uploader: Uploader
}

/**
 * Panel instances currently mounted. Read during the first render to tell a
 * replacement apart from a genuine open: when the user switches between
 * ideas the previous panel is still mounted while its replacement renders,
 * and the replacement must not replay the entry animation.
 */
let mountedPanelCount = 0

/**
 * Inline side panel presentation of the Idea Composer. Rendered in place
 * (no portal, no overlay) so the page can lay it out next to its content.
 */
export const IdeaComposerPanel = React.forwardRef<
  HTMLElement,
  IdeaComposerPanelProps
>((props, ref): JSX.Element => {
  const { onClose, uploader } = props
  const { hasUnsavedChanges, draftIdea } = useIdeaComposerState()

  const [isDiscardChangesDialogOpen, setIsDiscardChangesDialogOpen] =
    React.useState(false)

  const { ideas: ideasLimit } = useOrganizationLimits() ?? {}
  const { ideasCount } = useIdeasCount()
  const isIdeasLimitReached = ideasLimit && ideasCount >= ideasLimit
  const location = useLocation()
  const locationState = isIdeaComposerLocationState(location.state)
    ? location.state
    : undefined
  const isEditAction = locationState?.action === 'edit'

  const container = document.getElementById('portals-container')

  const [isReplacingPanel] = React.useState(() => mountedPanelCount > 0)
  React.useEffect(() => {
    mountedPanelCount += 1
    return (): void => {
      mountedPanelCount -= 1
    }
  }, [])

  const closeButtonRef = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    // A replacement panel must not steal focus from the list; only a
    // genuinely opened panel moves focus to its close button.
    if (!isReplacingPanel) {
      closeButtonRef.current?.focus()
    }
  }, [isReplacingPanel])

  /**
   * Check for unsaved changes before closing the composer.
   */
  const handleClose = React.useCallback(
    (event?: React.MouseEvent | React.KeyboardEvent) => {
      event?.stopPropagation()
      event?.preventDefault()
      if (hasUnsavedChanges()) {
        setIsDiscardChangesDialogOpen(true)
      } else {
        onClose()
      }
    },
    [hasUnsavedChanges, onClose],
  )

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape') {
      handleClose(event)
    }
  }

  const shouldReduceMotion = useReducedMotion()
  // Zero offset keeps a plain fade for users who prefer reduced motion.
  const slideOffset = shouldReduceMotion ? 0 : 32

  if (isIdeasLimitReached && !isEditAction) {
    return <IdeasLimitReachedModal open={true} onClose={onClose} />
  }

  const panelLabel = draftIdea?.id
    ? draftIdea.content.title
      ? `Editing idea: ${draftIdea.content.title}`
      : 'Editing idea'
    : 'Creating idea'

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <m.aside
      ref={ref}
      className={styles.panel}
      aria-label={panelLabel}
      data-testid="idea-composer-panel"
      onKeyDown={handleKeyDown}
      initial={isReplacingPanel ? false : { opacity: 0, x: slideOffset }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: slideOffset }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      <IdeaEditor
        container={container}
        uploader={uploader}
        className={styles.editor}
        presentation="panel"
        headerAction={
          <IconButton
            ref={closeButtonRef}
            label="Close composer"
            variant="tertiary"
            size="small"
            onClick={handleClose}
          >
            <XIcon />
          </IconButton>
        }
      />

      <AIAssistantPanelOverlay />

      {/* Discard changes confirmation dialog */}
      <ConfirmCloseModal
        open={isDiscardChangesDialogOpen}
        onOpenChange={setIsDiscardChangesDialogOpen}
        onCancel={(event: React.MouseEvent): void => {
          event.preventDefault()
          event.stopPropagation()
          setIsDiscardChangesDialogOpen(false)
        }}
        onConfirmClose={(): void => {
          setIsDiscardChangesDialogOpen(false)
          onClose()
        }}
      />
    </m.aside>
  )
})

IdeaComposerPanel.displayName = 'IdeaComposerPanel'
